import { DateTime } from 'luxon';
import moment from 'moment-timezone';

let formats = null;

const countryDateFormats = {
  'en-US': 'MM/dd/yyyy',
};

const countryTimeFormats = {
  'en-US': { timeFormat: 'hh:mm A', is12Hour: true },
};

function getPredefinedDateFormat() {
  const language = navigator.language;
  const languageCode = language.split('-')[0];
  const countryCode = language.split('-')[1];
  
  return countryDateFormats[language] || 
         countryDateFormats[countryCode] || 
         countryDateFormats[languageCode] || 
         null;
}

function getPredefinedTimeFormat() {
  const language = navigator.language;
  const languageCode = language.split('-')[0];
  const countryCode = language.split('-')[1];
  
  return countryTimeFormats[language] || 
         countryTimeFormats[countryCode] || 
         countryTimeFormats[languageCode] || 
         null;
}

function detectTimeFormat() {
    // Check for predefined format first
    const predefinedFormat = getPredefinedTimeFormat();
    if (predefinedFormat) {
        return predefinedFormat;
    }

    try {
        const formatter = new Intl.DateTimeFormat(navigator.language, {
            timeStyle: 'short'
        });

        // Use 2:30 PM as test time to detect 12/24 hour format
        const testDate = new Date(2024, 0, 15, 14, 30, 0);
        const parts = formatter.formatToParts(testDate);

        // Check if any part is dayPeriod (AM/PM indicator)
        const hasDayPeriod = parts.some(part => part.type === 'dayPeriod');

        const format = parts.map((part) => {
            switch (part.type) {
                case 'hour':
                    return (hasDayPeriod ? 'h' : 'H').repeat(part.value.length);
                case 'minute':
                    return 'mm';
                case 'literal':
                    return part.value;
                case 'dayPeriod':
                    return part.value ? 'A' : '';
                default:
                    return '';
            }
        }).join('').trim();

        const timeFormat = format || "HH:mm";

        return {
            timeFormat,
            is12Hour: hasDayPeriod
        };
    } catch (e) {
        return {
            timeFormat: "hh:mm A",
            is12Hour: true
        };
    }
}

function detectDateFormat() {
    // Check for predefined format first
    const predefinedFormat = getPredefinedDateFormat();
    if (predefinedFormat) {
        return predefinedFormat;
    }

    try {
        const formatter = new Intl.DateTimeFormat(navigator.language, {
            dateStyle: 'short',
        });

        // Use the same test date as before for consistency
        const testDate = new Date(2024, 0, 15); // January 15, 2024
        const parts = formatter.formatToParts(testDate);

        const format = parts.map((part) => {
            let result;
            switch (part.type) {
                case 'year':
                    result = 'y'.repeat(part.value.length);
                    break;
                case 'month':
                    result = 'M'.repeat(part.value.length);
                    break;
                case 'day':
                    result = 'd'.repeat(part.value.length);
                    break;
                case 'literal':
                    result = part.value;
                    break;
                default:
                    result = '';
                    break;
            }
            return result;
        }).join('').trim();

        const finalFormat = format || "MM/dd/yyyy";

        return finalFormat;
    } catch (e) {
        return "MM/dd/yyyy"; // fallback
    }
}

function detectFormats() {
    if (formats) {
        return formats;
    }

    try {
        const timeResult = detectTimeFormat();
        const dateFormat = detectDateFormat();

        const dateTimeFormat = `${dateFormat} ${timeResult.timeFormat}`;

        // Store the results
        formats = {
            is12Hour: timeResult.is12Hour,
            dateFormat,
            timeFormat: timeResult.timeFormat,
            dateTimeFormat,
            locale: navigator.language || 'en-US'
        };

        console.log('Final detected formats:', formats);

        return formats;

    } catch (e) {
        // Fallback with MM/dd/yyyy
        formats = {
            is12Hour: true,
            dateFormat: "MM/dd/yyyy",
            timeFormat: "hh:mm A",
            dateTimeFormat: "MM/dd/yyyy hh:mm A",
            locale: 'en-US'
        };

        return formats;
    }
}

function getDateTimeFormat() {
    const formats = detectFormats();
    return formats.dateTimeFormat;
}

function getDateFormat() {
    const formats = detectFormats();
    return formats.dateFormat;
}

function getTimeFormat() {
    const formats = detectFormats();
    return formats.timeFormat;
}

function formatDateToLocalFormat(date) {
  if(!date) return '—'
  if (typeof date === 'string') {
    if (!date.includes('T') && !date.includes(' ')) {
        const [year, month, day] = date.split('-').map(Number);
        //If it's just a date, create the date in local time (00:00)
        date = new Date(year, month - 1, day);
    } else {
        // If it includes time
        date = new Date(date);
    }
}
  const formattedDate = DateTime.fromJSDate(date).toFormat(detectFormats().dateFormat);
  return formattedDate;
}

function formatTimeToLocalFormat(time) {
  if(!time) return '—'
  return moment(time).format(detectFormats().timeFormat);
}

function formatDateTimeToLocalFormat(dateTime) {
  if(!dateTime) return '—'
  const formatter = new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  if (typeof dateTime === 'string') dateTime = new Date(dateTime);
  const formattedDateTime = formatter.format(dateTime);
  return formattedDateTime;
}

/**
* Generates time options for dropdowns/selectors
* @param {Object} options - Configuration options
* @param {number} options.minuteInterval - Minute intervals (default: 15)
* @param {boolean} options.includeSeconds - Include seconds in format (default: false)
* @param {boolean} options.use24Hour - Force 24-hour format (default: null - auto-detect)
* @param {string} options.startHour - Starting hour (default: '00')
* @param {string} options.endHour - Ending hour (default: '23')
* @returns {Array} Array of time options with time and label properties
*/
function generateTimeOptions(options = {}) {
    const {
        minuteInterval = 15,
        includeSeconds = false,
        use24Hour = null,
        startHour = '00',
        endHour = '23'
    } = options;

    const timeOptions = [];
    const startHourNum = parseInt(startHour);
    const endHourNum = parseInt(endHour);

    // Determine format from plugin or override
    const is12Hour = use24Hour !== null ? !use24Hour : (detectFormats().is12Hour);

    // Generate hour range
    const hours = [];
    for (let i = startHourNum; i <= endHourNum; i++) {
        hours.push(i.toString().padStart(2, '0'));
    }

    // Generate minute intervals
    const minutes = [];
    for (let i = 0; i < 60; i += minuteInterval) {
        minutes.push(i.toString().padStart(2, '0'));
    }

    for (const hour of hours) {
        for (const minute of minutes) {
            const timeBase = `${hour}:${minute}${includeSeconds ? ':00' : ''}`;

            if (is12Hour) {
                // 12-hour format with AM/PM
                let twelveTime = parseInt(hour) % 12;
                if (twelveTime === 0) twelveTime = 12;
                const formattedHour = twelveTime.toString().padStart(2, '0');
                const amPm = parseInt(hour) >= 12 ? 'PM' : 'AM';
                const timeLabel = `${formattedHour}:${minute}${includeSeconds ? ':00' : ''} ${amPm}`;

                timeOptions.push({
                    time: timeLabel,
                    label: timeLabel,
                    value24: timeBase,
                    hour24: hour,
                    hour12: formattedHour,
                    minute,
                    amPm
                });
            } else {
                // 24-hour format
                timeOptions.push({
                    time: timeBase,
                    label: timeBase,
                    value24: timeBase,
                    hour24: hour,
                    minute
                });
            }
        }
    }

    return timeOptions;
}

export default {
    install(Vue) {
        const formats = detectFormats();

        Vue.prototype.$dateTimeFormat = formats.dateTimeFormat;
        Vue.prototype.$dateFormat = formats.dateFormat;
        Vue.prototype.$timeFormat = formats.timeFormat;
        Vue.prototype.$is12Hour = formats.is12Hour;
        Vue.prototype.$locale = formats.locale;

        Vue.prototype.$getDateTimeFormat = getDateTimeFormat;
        Vue.prototype.$getDateFormat = getDateFormat;
        Vue.prototype.$getTimeFormat = getTimeFormat;
        Vue.prototype.$formatDateToLocalFormat = formatDateToLocalFormat;
        Vue.prototype.$formatTimeToLocalFormat = formatTimeToLocalFormat;
        Vue.prototype.$formatDateTimeToLocalFormat = formatDateTimeToLocalFormat;

        // Time Options Generator
        Vue.prototype.$generateTimeOptions = generateTimeOptions;

    }
};

export {
    // Format detection
    detectFormats,
    getDateTimeFormat,
    getDateFormat,
    getTimeFormat,

    // Time utilities
    generateTimeOptions,
    
    // Format utilities
    formatDateToLocalFormat,
    formatTimeToLocalFormat,
    formatDateTimeToLocalFormat,
};
